import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { ShoppingCart, Search, Package, Plus, Minus, Loader2, CheckCircle, AlertTriangle, X, Copy, Check } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

export default function CustomerStore() {
    const [gyms, setGyms] = useState([]);
    const [selectedGymId, setSelectedGymId] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [cart, setCart] = useState({});
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState('');
    const [notes, setNotes] = useState('');
    const [placing, setPlacing] = useState(false);
    const [placeError, setPlaceError] = useState('');
    const [placeSuccess, setPlaceSuccess] = useState(false);
    const [productDetail, setProductDetail] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [transferDetails, setTransferDetails] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    useEffect(() => {
        const fetchGyms = async () => {
            try {
                const res = await api.get('/customer/store/my-gyms');
                setGyms(res.data.data || []);
                if (res.data.data?.length > 0) {
                    setSelectedGymId(res.data.data[0].id);
                }
            } catch {} finally { setLoading(false); }
        };
        fetchGyms();
    }, []);

    useEffect(() => {
        if (!selectedGymId) return;
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const res = await api.get('/customer/store/products', { params: { gym_id: selectedGymId, search } });
                setProducts(res.data.data || []);
            } catch {} finally { setLoading(false); }
        };
        fetchProducts();
    }, [selectedGymId, search]);

    useEffect(() => {
        if (!selectedGymId) return;
        const fetchPaymentMethods = async () => {
            try {
                const res = await api.get('/customer/store/payment-methods', { params: { gym_id: selectedGymId } });
                setPaymentMethods(res.data.data || []);
                if (res.data.data?.length > 0) {
                    setSelectedMethod(res.data.data[0].id);
                }
            } catch {}
        };
        fetchPaymentMethods();
    }, [selectedGymId]);

    useEffect(() => {
        if (!selectedGymId || selectedMethod !== 'transfer') {
            setTransferDetails(null);
            return;
        }
        const fetchTransfer = async () => {
            try {
                const res = await api.get(`/public/gym/${selectedGymId}/transfer-details`);
                setTransferDetails(res.data.data?.details || null);
            } catch {
                setTransferDetails(null);
            }
        };
        fetchTransfer();
    }, [selectedGymId, selectedMethod]);

    // Persist cart per-gym in localStorage
    useEffect(() => {
        if (selectedGymId) {
            try {
                const allCarts = JSON.parse(localStorage.getItem('gymcard_cart') || '{}');
                setCart(allCarts[String(selectedGymId)] || {});
            } catch {
                setCart({});
            }
        }
    }, [selectedGymId]);

    useEffect(() => {
        if (selectedGymId) {
            try {
                const allCarts = JSON.parse(localStorage.getItem('gymcard_cart') || '{}');
                allCarts[String(selectedGymId)] = cart;
                localStorage.setItem('gymcard_cart', JSON.stringify(allCarts));
            } catch {}
        }
    }, [cart, selectedGymId]);

    const addToCart = (product) => {
        setCart(prev => ({
            ...prev,
            [product.id]: (prev[product.id] || 0) + 1,
        }));
    };

    const removeFromCart = (productId) => {
        setCart(prev => {
            const next = { ...prev };
            if (next[productId] <= 1) {
                delete next[productId];
            } else {
                next[productId]--;
            }
            return next;
        });
    };

    const cartItems = Object.entries(cart)
        .map(([id, qty]) => {
            const product = products.find(p => String(p.id) === id);
            return product ? { ...product, quantity: qty } : null;
        })
        .filter(Boolean);

    const cartTotal = cartItems.reduce((sum, item) => sum + parseFloat(item.price || 0) * item.quantity, 0);
    const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    const handlePlaceOrder = async () => {
        if (cartItems.length === 0 || !selectedMethod) return;

        // Client-side stock check
        const stockErrors = cartItems
            .filter(item => item.stock < item.quantity)
            .map(item => `"${item.name}" solo tiene ${item.stock} unidad${item.stock !== 1 ? 'es' : ''} disponible.`);

        if (stockErrors.length > 0) {
            setPlaceError(stockErrors.join(' '));
            return;
        }

        setPlacing(true);
        setPlaceError('');
        setPlaceSuccess(false);
        try {
            const items = cartItems.map(item => ({
                product_id: item.id,
                quantity: item.quantity,
            }));
            await api.post('/customer/store/orders', {
                gym_id: selectedGymId,
                items,
                payment_method: selectedMethod,
                notes: notes.trim() || undefined,
            });
            setPlaceSuccess(true);
            setCart({});
            setNotes('');
            setCartOpen(false);
            setTimeout(() => setPlaceSuccess(false), 4000);
        } catch (err) {
            setPlaceError(err.response?.data?.message || 'Error al realizar el pedido.');
        } finally { setPlacing(false); }
    };

    const stockBadge = (stock) => {
        if (stock <= 0) return { text: 'Sin stock', className: 'bg-red-950/60 text-red-400 border-red-900/50' };
        if (stock <= 4) return { text: 'Stock bajo', className: 'bg-yellow-950/60 text-yellow-400 border-yellow-900/50' };
        return { text: 'En stock', className: 'bg-green-950/60 text-green-400 border-green-900/50' };
    };

    const openProductDetail = async (product) => {
        try {
            const res = await api.get(`/customer/store/products/${product.id}`);
            setProductDetail(res.data.data);
        } catch {
            setProductDetail(product);
        }
        setDetailOpen(true);
    };

    const handleCopy = async (text, field) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch {}
    };

    const selectedGym = gyms.find(g => g.id === selectedGymId);
    const selectedMethodObj = paymentMethods.find(m => m.id === selectedMethod);

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Tienda</h1>
                <div className="flex items-center gap-3">
                    {cartCount > 0 && (
                        <span className="text-sm text-white/80 hidden sm:inline">{cartCount} producto{cartCount !== 1 ? 's' : ''} — ${cartTotal.toFixed(2)}</span>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#3a3a3f] text-white hover:bg-white/5 relative"
                        onClick={() => setCartOpen(true)}
                    >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Carrito
                        {cartCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-bold text-black bg-white rounded-full">
                                {cartCount}
                            </span>
                        )}
                    </Button>
                </div>
            </div>

            {gyms.length > 1 && (
                <div className="mb-4">
                    <select
                        className="h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 text-sm text-white max-w-xs"
                        value={selectedGymId ?? ''}
                        onChange={e => { setSelectedGymId(parseInt(e.target.value)); setCart({}); setPlaceSuccess(false); }}
                    >
                        {gyms.map(g => (
                            <option key={g.id} value={g.id} className="bg-[#0a0a0a] text-white">{g.name}</option>
                        ))}
                    </select>
                </div>
            )}

            {placeSuccess && (
                <div className="mb-4 p-3 rounded border border-green-800 bg-green-950/30 text-green-300 text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Pedido realizado con éxito. Podés ver el estado en "Mis Pedidos".
                </div>
            )}

            {placeError && (
                <div className="mb-4 p-3 rounded border border-red-800 bg-red-950/30 text-red-300 text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    {placeError}
                </div>
            )}

            <div className="mb-4">
                <div className="relative max-w-sm">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
                    <Input placeholder="Buscar productos..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                </div>
            </div>

            {loading ? (
                <p className="text-white/60 text-center py-8">Cargando productos...</p>
            ) : products.length === 0 ? (
                <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                    <CardContent className="py-12 text-center">
                        <Package className="h-12 w-12 text-white/60 mx-auto mb-4" />
                        <p className="text-white/60">No hay productos disponibles en este gimnasio.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {products.map(product => {
                        const sb = stockBadge(product.stock);
                        const qty = cart[product.id] || 0;
                        return (
                            <div key={product.id} className="group bg-[#0a0a0a] border border-[#3a3a3f] rounded-lg overflow-hidden hover:border-white/20 transition-colors">
                                <button type="button" onClick={() => openProductDetail(product)} className="block w-full text-left">
                                    <div className="aspect-square bg-[#111] flex items-center justify-center overflow-hidden">
                                        {product.image ? (
                                            <img src={`/storage/${product.image}`} alt={product.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                        ) : (
                                            <Package className="h-12 w-12 text-white/20" />
                                        )}
                                    </div>
                                </button>
                                <div className="p-3">
                                    <button type="button" onClick={() => openProductDetail(product)} className="block w-full text-left">
                                        <h3 className="font-semibold text-white text-sm leading-tight truncate">{product.name}</h3>
                                        {product.sku && <p className="text-[10px] text-white/40 mt-0.5 font-mono">{product.sku}</p>}
                                    </button>
                                    <div className="flex items-center justify-between mt-2">
                                        <span className="text-base font-bold text-white">${parseFloat(product.price || 0).toFixed(2)}</span>
                                        <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-medium", sb.className)}>
                                            {sb.text}
                                        </span>
                                    </div>
                                    <div className="mt-3 flex items-center gap-2">
                                        {qty > 0 ? (
                                            <div className="flex items-center gap-1 flex-1">
                                                <Button variant="outline" size="icon" className="h-8 w-8 border-[#3a3a3f] text-white hover:bg-white/5 shrink-0" onClick={() => removeFromCart(product.id)}>
                                                    <Minus className="h-3 w-3" />
                                                </Button>
                                                <span className="text-sm font-medium text-white w-6 text-center">{qty}</span>
                                                <Button variant="outline" size="icon" className="h-8 w-8 border-[#3a3a3f] text-white hover:bg-white/5 shrink-0" onClick={() => addToCart(product)} disabled={product.stock <= qty}>
                                                    <Plus className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button variant="outline" size="sm" className="w-full text-xs border-[#3a3a3f] text-white hover:bg-white/5" onClick={() => addToCart(product)} disabled={product.stock <= 0}>
                                                <Plus className="h-3.5 w-3.5 mr-1.5" />AGREGAR
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Cart Drawer / Mini Cart */}
            <Dialog open={cartOpen} onOpenChange={setCartOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white sm:max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
                            <ShoppingCart className="h-5 w-5" />
                            Carrito
                            {cartCount > 0 && <Badge variant="default" className="text-xs">{cartCount}</Badge>}
                        </DialogTitle>
                    </DialogHeader>

                    {cartItems.length === 0 ? (
                        <p className="text-sm text-white/60 text-center py-8">Tu carrito está vacío</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2 max-h-[240px] overflow-y-auto">
                                {cartItems.map(item => (
                                    <div key={item.id} className="flex items-center justify-between p-2 rounded bg-white/5 border border-[#3a3a3f]">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm text-white truncate">{item.name}</p>
                                            <p className="text-xs text-white/60">${parseFloat(item.price || 0).toFixed(2)} x {item.quantity}</p>
                                        </div>
                                        <div className="flex items-center gap-1 ml-2 shrink-0">
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-white hover:bg-white/5" onClick={() => removeFromCart(item.id)}>
                                                <Minus className="h-3 w-3" />
                                            </Button>
                                            <span className="text-sm font-medium text-white w-5 text-center">{item.quantity}</span>
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-white hover:bg-white/5" onClick={() => addToCart(item)} disabled={item.stock <= item.quantity}>
                                                <Plus className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t border-[#3a3a3f] pt-3 space-y-3">
                                <div className="flex justify-between text-sm">
                                    <span className="text-white/80">Total</span>
                                    <span className="font-bold text-white">${cartTotal.toFixed(2)}</span>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Método de pago</Label>
                                    <select
                                        className="w-full h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 text-sm text-white"
                                        value={selectedMethod}
                                        onChange={e => setSelectedMethod(e.target.value)}
                                    >
                                        {paymentMethods.map(m => (
                                            <option key={m.id} value={m.id} className="bg-[#0a0a0a] text-white capitalize">{m.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedMethod === 'transfer' && transferDetails && (
                                    <div className="p-3 rounded border border-[#3a3a3f] bg-white/5 space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Datos de transferencia</Label>
                                        <p className="text-xs text-white/80 whitespace-pre-line leading-relaxed">{transferDetails}</p>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full text-xs text-white/60 hover:text-white hover:bg-white/5"
                                            onClick={() => handleCopy(transferDetails, 'details')}
                                        >
                                            {copiedField === 'details' ? <Check className="h-3.5 w-3.5 mr-1.5 text-green-400" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                                            {copiedField === 'details' ? 'Copiado' : 'Copiar datos'}
                                        </Button>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Notas (opcional)</Label>
                                    <textarea
                                        className="flex min-h-[60px] w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white"
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                        placeholder="Alguna indicación..."
                                    />
                                </div>

                                <Button
                                    className="w-full"
                                    disabled={placing || cartItems.length === 0}
                                    onClick={handlePlaceOrder}
                                >
                                    {placing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {placing ? 'Procesando...' : 'Hacer Pedido'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Product Detail Dialog */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">{productDetail?.name}</DialogTitle>
                    </DialogHeader>
                    {productDetail && (
                        <div className="space-y-3">
                            {productDetail.image && (
                                <div className="aspect-video bg-[#111] rounded overflow-hidden flex items-center justify-center">
                                    <img src={`/storage/${productDetail.image}`} alt={productDetail.name} className="h-full w-full object-cover" />
                                </div>
                            )}
                            {productDetail.description && (
                                <p className="text-sm text-white/80">{productDetail.description}</p>
                            )}
                            <div className="flex items-center justify-between">
                                <span className="text-2xl font-bold text-white">${parseFloat(productDetail.price || 0).toFixed(2)}</span>
                                {productDetail.stock <= 0 ? (
                                    <Badge variant="outline" className="text-red-400 border-red-900/50">Sin stock</Badge>
                                ) : (
                                    <Badge variant="outline" className="text-white/60">Stock: {productDetail.stock}</Badge>
                                )}
                            </div>
                            {productDetail.stock > 0 && (
                                <Button className="w-full" onClick={() => { addToCart(productDetail); setDetailOpen(false); }}>
                                    <Plus className="h-4 w-4 mr-2" />Agregar al carrito
                                </Button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
